import React, { useEffect, useRef, useState } from 'react';
import {
    applyPowerUnitLabel,
    energyScaleFromKwh,
    formatEnergyKwh,
    formatPowerKw,
    powerScaleFromKw,
} from '../../utils/formatEnergyUnits';

export const SEASON_COLORS   = { spring: '#22c55e', summer: '#f97316', fall: '#a855f7', winter: '#38bdf8' };
export const ALL_SEASONS     = ['all', 'spring', 'summer', 'fall', 'winter'];
export const ALL_DAY_TYPES   = ['all', 'weekday', 'weekend'];
export const MONTH_LABELS    = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
export const MONTH_DAY_STARTS = [0,31,59,90,120,151,181,212,243,273,304,334]; // day index of month start

export function computeLoadFactor(totalEnergyKwh, peakKw, hourCount = 8760) {
    const e = Number(totalEnergyKwh), p = Number(peakKw), h = Number(hourCount);
    if (!isFinite(e) || !isFinite(p) || p <= 0) return null;
    return (e / h) / p;
}

// Build 365-value array of daily energy totals from 8760-hour profile
export function computeDailyEnergy(hourlyProfile8760) {
    const vals = Array.isArray(hourlyProfile8760) ? hourlyProfile8760 : [];
    const daily = [];
    for (let d = 0; d < 365; d++) {
        let sum = 0;
        for (let h = 0; h < 24; h++) sum += Number(vals[d * 24 + h] || 0);
        daily.push(sum);
    }
    return daily;
}

// Build seasonal weekly profiles (168 hours, Mon-based) from 8760-hour profile
export function buildSeasonalWeeklyProfiles(hourlyProfile, forecastYear) {
    const vals = Array.isArray(hourlyProfile) ? hourlyProfile.map(v => Number(v || 0)) : [];
    if (!vals.length) return { all: [] };
    const W = 168, D = 24;
    const base = new Date(Date.UTC(Number.isFinite(Number(forecastYear)) ? Number(forecastYear) : 2027, 0, 1));
    const buckets = {};
    ['all','spring','summer','fall','winter'].forEach(s => {
        buckets[s] = { totals: new Array(W).fill(0), counts: new Array(W).fill(0) };
    });
    const seasonForMonth = (m) => {
        if ([3,4,5].includes(m)) return 'spring';
        if ([6,7,8].includes(m)) return 'summer';
        if ([9,10,11].includes(m)) return 'fall';
        return 'winter';
    };
    vals.forEach((v, i) => {
        const dayOffset = Math.floor(i / D);
        const hourOfDay = i % D;
        const date = new Date(base); date.setUTCDate(base.getUTCDate() + dayOffset);
        const dow = date.getUTCDay();
        const whi = ((dow + 6) % 7) * D + hourOfDay;
        const season = seasonForMonth(date.getUTCMonth() + 1);
        buckets.all.totals[whi] += v; buckets.all.counts[whi]++;
        buckets[season].totals[whi] += v; buckets[season].counts[whi]++;
    });
    return Object.fromEntries(
        Object.entries(buckets).map(([s, b]) => [
            s, b.totals.map((t, i) => b.counts[i] > 0 ? +(t / b.counts[i]).toFixed(6) : 0)
        ])
    );
}

// ─── Small inline chart (sidebar) ────────────────────────────────────────────

export function DailyProfileChart({ values = [], color = '#f97316', title, width = 320 }) {
    const safe = Array.isArray(values) ? values.slice(0, 24).map(v => Number(v || 0)) : [];
    if (!safe.length || !safe.some(v => v > 0))
        return <p className="text-xs text-gray-400 italic py-2">No data for this period.</p>;
    const W = width, H = 148;
    const pL = 48, pR = 18, pT = 8, pB = 28;
    const iW = W - pL - pR, iH = H - pT - pB;
    const max = Math.max(...safe, 1e-9);
    const yScale = powerScaleFromKw(safe);
    const xf = (i) => pL + (iW * i) / 23;
    const yf = (v) => pT + iH - (v / max) * iH;
    const pts = safe.map((v, i) => `${xf(i)},${yf(v)}`).join(' ');
    const area = `${pL},${pT + iH} ${pts} ${W - pR},${pT + iH}`;
    const labeledHours = [0, 6, 12, 18, 23];
    const hourAnchor = (h) => (h === 0 ? 'start' : h === 23 ? 'end' : 'middle');
    const axisFill = '#374151';
    const tickStroke = '#6b7280';
    return (
        <div className="w-full overflow-visible">
            {title && <p className="px-1 text-xs font-medium text-gray-600 mb-1">{title}</p>}
            <svg
                width="100%"
                height={H}
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                className="overflow-visible block w-full"
                role="img"
                aria-label={title || 'Daily load profile'}
            >
                {labeledHours.map(h => (
                    <line
                        key={`grid-${h}`}
                        x1={xf(h)} y1={pT}
                        x2={xf(h)} y2={pT + iH}
                        stroke="#d1d5db"
                        strokeWidth="1"
                    />
                ))}
                {[0, max / 2, max].map((tv, i) => {
                    const y = yf(tv);
                    return (
                        <g key={i}>
                            <line x1={pL} y1={y} x2={W - pR} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x={pL - 6} y={y + 3} textAnchor="end" fontSize="10" fill={axisFill}>
                                {yScale.formatTick(tv)}
                            </text>
                        </g>
                    );
                })}
                {labeledHours.map(h => (
                    <g key={`tick-${h}`}>
                        <line
                            x1={xf(h)} y1={pT + iH}
                            x2={xf(h)} y2={pT + iH + 5}
                            stroke={tickStroke}
                            strokeWidth="1"
                        />
                        <text
                            x={xf(h)}
                            y={pT + iH + 17}
                            textAnchor={hourAnchor(h)}
                            fontSize="10"
                            fill={axisFill}
                        >
                            {String(h).padStart(2, '0')}:00
                        </text>
                    </g>
                ))}
                <line x1={pL} y1={pT + iH} x2={W - pR} y2={pT + iH} stroke={tickStroke} strokeWidth="1" />
                <line x1={pL} y1={pT}      x2={pL}      y2={pT + iH} stroke={tickStroke} strokeWidth="1" />
                <polygon points={area} fill={color} fillOpacity="0.12" />
                <polyline fill="none" stroke={color} strokeWidth="2" points={pts} />
                <text
                    x={8}
                    y={pT + iH / 2}
                    transform={`rotate(-90,8,${pT + iH / 2})`}
                    textAnchor="middle"
                    fontSize="10"
                    fill={axisFill}
                >
                    {yScale.unit}
                </text>
            </svg>
        </div>
    );
}

// ─── Interactive scrollable timeline (modal) ──────────────────────────────────

export function InteractiveTimelineChart({
    values = [],
    color = '#2563eb',
    width = 1400,
    height = 300,
    zoom = 1,
    xTickPositions = [],
    xTickLabels = [],
    xAxisLabel = 'Timeline',
    yAxisLabel = 'Load',
    showWeekMarkers = false,
    weekMarkerIntervalHours = 168,
    enableAdaptiveGrid = false,
    axisStartDate = null,
}) {
    const scrollRef = useRef(null);
    const dragging = useRef(false);
    const dragState = useRef({ startX: 0, startLeft: 0 });
    const safe = Array.isArray(values) ? values.map(v => Number(v || 0)) : [];

    useEffect(() => {
        const up = () => { dragging.current = false; };
        window.addEventListener('mouseup', up);
        return () => window.removeEventListener('mouseup', up);
    }, []);

    if (!safe.length) return <div className="text-sm text-gray-400">No data available.</div>;

    const cW = Math.max(width, Math.round(width * Number(zoom || 1)));
    const pL = 60, pR = 28, pT = 14, pB = 40;
    const iW = cW - pL - pR, iH = height - pT - pB;
    const max = Math.max(...safe, 1e-9);
    const yScale = powerScaleFromKw(safe);
    const yTicks = [0, max / 2, max];
    const xf = (i) => pL + (iW * i) / Math.max(safe.length - 1, 1);
    const yf = (v) => pT + iH - (v / max) * iH;
    const pts = safe.map((v, i) => `${xf(i)},${yf(v)}`).join(' ');
    const area = `${pL},${pT + iH} ${pts} ${cW - pR},${pT + iH}`;
    const showHourTicks = safe.length === 24;
    const axisFill = '#374151';
    const tickStroke = '#6b7280';

    const weekMarkers = [];
    if (showWeekMarkers && safe.length > 1) {
        const px = (iW * weekMarkerIntervalHours) / Math.max(safe.length - 1, 1);
        const labelEvery = Math.max(1, Math.ceil(60 / Math.max(px, 0.1)));
        const total = Math.floor((safe.length - 1) / weekMarkerIntervalHours);
        const baseDateObj = axisStartDate ? (axisStartDate instanceof Date ? axisStartDate : new Date(axisStartDate)) : null;
        for (let w = 0; w <= total; w++) {
            const hi = Math.min(safe.length - 1, w * weekMarkerIntervalHours);
            const label = (() => {
                if (!baseDateObj) return `W${w + 1}`;
                const d = new Date(baseDateObj); d.setUTCHours(d.getUTCHours() + hi);
                return d.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
            })();
            weekMarkers.push({ hi, label, showLabel: w % labelEvery === 0, isQ: w > 0 && w % 13 === 0 });
        }
    }

    const onMouseDown = (e) => {
        if (!scrollRef.current) return;
        dragging.current = true;
        dragState.current = { startX: e.clientX, startLeft: scrollRef.current.scrollLeft };
    };
    const onMouseMove = (e) => {
        if (!dragging.current || !scrollRef.current) return;
        scrollRef.current.scrollLeft = dragState.current.startLeft - (e.clientX - dragState.current.startX);
    };

    return (
        <div
            ref={scrollRef}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            className="overflow-x-auto rounded border border-gray-200 bg-white cursor-grab active:cursor-grabbing select-none"
        >
            <svg width={cW} height={height} style={{ display: 'block' }}>
                {yTicks.map((tv, i) => {
                    const y = yf(tv);
                    return (
                        <g key={i}>
                            <line x1={pL} y1={y} x2={cW - pR} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x={pL - 6} y={y + 3} textAnchor="end" fontSize="10" fill={axisFill}>
                                {yScale.formatTick(tv)}
                            </text>
                        </g>
                    );
                })}
                {showHourTicks && [0, 6, 12, 18, 23].map((h) => (
                    <line
                        key={`hour-${h}`}
                        x1={xf(h)} y1={pT}
                        x2={xf(h)} y2={pT + iH}
                        stroke="#d1d5db"
                        strokeWidth="1"
                    />
                ))}
                {(xTickPositions || []).map((pos, i) => {
                    const x = xf(pos);
                    const isFirst = i === 0;
                    const isLast = i === (xTickPositions.length - 1);
                    return (
                        <g key={i}>
                            <line x1={x} y1={pT + iH} x2={x} y2={pT + iH + 4} stroke={tickStroke} strokeWidth="1" />
                            <text
                                x={x}
                                y={pT + iH + 16}
                                textAnchor={isFirst ? 'start' : isLast ? 'end' : 'middle'}
                                fontSize="10"
                                fill={axisFill}
                            >
                                {xTickLabels[i] || ''}
                            </text>
                        </g>
                    );
                })}
                {weekMarkers.map(({ hi, label, showLabel, isQ }) => {
                    const x = xf(hi);
                    return (
                        <g key={hi}>
                            <line x1={x} y1={pT} x2={x} y2={pT + iH}
                                stroke={isQ ? '#93c5fd' : '#dbeafe'}
                                strokeWidth={isQ ? 1.4 : 1}
                                strokeDasharray={isQ ? '5 4' : '2 4'} />
                            {showLabel && (
                                <text x={x + 2} y={pT + 10} textAnchor="start" fontSize="9" fill="#60a5fa">{label}</text>
                            )}
                        </g>
                    );
                })}
                <line x1={pL} y1={pT + iH} x2={cW - pR} y2={pT + iH} stroke={tickStroke} strokeWidth="1" />
                <line x1={pL} y1={pT}      x2={pL}      y2={pT + iH} stroke={tickStroke} strokeWidth="1" />
                <polygon points={area} fill="#93c5fd" fillOpacity="0.18" />
                <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
                <text x={16} y={pT + iH / 2} transform={`rotate(-90,16,${pT + iH / 2})`}
                    textAnchor="middle" fontSize="10" fill={axisFill}>{applyPowerUnitLabel(yAxisLabel, yScale.unit)}</text>
                <text x={pL + iW / 2} y={height - 6} textAnchor="middle" fontSize="10" fill={axisFill}>{xAxisLabel}</text>
            </svg>
        </div>
    );
}

// 365-day energy bar/line chart
export function DailyEnergyChart({ dailyValues = [], color = '#2563eb' }) {
    if (!dailyValues.length) return <p className="text-sm text-gray-400">No daily data.</p>;
    const W = 1100, H = 220, pL = 60, pR = 20, pT = 14, pB = 36;
    const iW = W - pL - pR, iH = H - pT - pB;
    const max = Math.max(...dailyValues, 1e-9);
    const yScale = energyScaleFromKwh(dailyValues);
    const xf = (d) => pL + (iW * d) / (dailyValues.length - 1);
    const yf = (v) => pT + iH - (v / max) * iH;
    const pts = dailyValues.map((v, i) => `${xf(i)},${yf(v)}`).join(' ');
    const area = `${pL},${pT + iH} ${pts} ${W - pR},${pT + iH}`;
    return (
        <div className="overflow-x-auto rounded border border-gray-200 bg-white">
            <svg width={W} height={H} style={{ display: 'block' }}>
                {[0, max / 2, max].map((tv, i) => {
                    const y = yf(tv);
                    return (
                        <g key={i}>
                            <line x1={pL} y1={y} x2={W - pR} y2={y} stroke="#e5e7eb" strokeWidth="1" />
                            <text x={pL - 6} y={y + 3} textAnchor="end" fontSize="10" fill="#6b7280">
                                {yScale.formatTick(tv)}
                            </text>
                        </g>
                    );
                })}
                {MONTH_DAY_STARTS.map((dayStart, mi) => {
                    const x = xf(dayStart);
                    return (
                        <g key={mi}>
                            <line x1={x} y1={pT} x2={x} y2={pT + iH} stroke="#e5e7eb" strokeWidth="1" strokeDasharray="3 4" />
                            <line x1={x} y1={pT + iH} x2={x} y2={pT + iH + 4} stroke="#9ca3af" strokeWidth="1" />
                            <text x={x + 2} y={pT + iH + 14} fontSize="10" fill="#6b7280">{MONTH_LABELS[mi]}</text>
                        </g>
                    );
                })}
                <line x1={pL} y1={pT + iH} x2={W - pR} y2={pT + iH} stroke="#9ca3af" strokeWidth="1" />
                <line x1={pL} y1={pT}      x2={pL}      y2={pT + iH} stroke="#9ca3af" strokeWidth="1" />
                <polygon points={area} fill={color} fillOpacity="0.12" />
                <polyline fill="none" stroke={color} strokeWidth="1.5" points={pts} />
                <text x={16} y={pT + iH / 2} transform={`rotate(-90,16,${pT + iH / 2})`}
                    textAnchor="middle" fontSize="9" fill="#9ca3af">{`${yScale.unit} / day`}</text>
                <text x={pL + iW / 2} y={H - 4} textAnchor="middle" fontSize="9" fill="#9ca3af">Day of year</text>
            </svg>
        </div>
    );
}

// ─── Full Hex Analytics Modal ─────────────────────────────────────────────────

export function HexAnalyticsModal({ open, onClose, h3_index, profile, subtitle = 'Hex Profile Analysis · CA 2027 MDHD' }) {
    const [tab, setTab] = useState('yearly');
    const [zoom, setZoom] = useState(2);
    const [season, setSeason] = useState('all');
    const [dayType, setDayType] = useState('all');

    useEffect(() => {
        if (!open) { setTab('yearly'); setZoom(2); setSeason('all'); setDayType('all'); }
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    // Close on Escape
    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, onClose]);

    if (!open || !profile) return null;

    const hourly8760 = Array.isArray(profile.hourly_profile_8760_kwh) ? profile.hourly_profile_8760_kwh : [];
    const peakDayProfile = Array.isArray(profile.peak_day_profile_24_kwh) ? profile.peak_day_profile_24_kwh : [];
    const seasonalProfiles = profile.seasonal_average_daily_profiles_24_kwh || {};
    const weeklyProfiles = buildSeasonalWeeklyProfiles(hourly8760, profile.forecast_year);
    const dailyEnergy = computeDailyEnergy(hourly8760);
    const avgDailyVals = seasonalProfiles?.[season]?.[dayType] || seasonalProfiles?.all?.all || [];
    const weeklyVals = weeklyProfiles[season] || weeklyProfiles.all || [];

    const annualKwh = Number(profile.annual_energy_kwh || profile.profile_summary?.total_energy_kwh || 0);
    const peakKw    = Number(profile.peak_power_kw    || profile.profile_summary?.peak_power_kw    || 0);
    const lf = computeLoadFactor(annualKwh, peakKw);

    const forecastYearLabel = Number(profile.forecast_year || 2027);
    const axisStart = new Date(Date.UTC(forecastYearLabel, 0, 1));
    const yearlyTickPos    = [0, 2160, 4344, 6552, 8759];
    const yearlyTickLabels = ['Jan', 'Apr', 'Jul', 'Oct', 'Dec'];
    const weeklyTickPos    = [0, 24, 48, 72, 96, 120, 144, 167];
    const weeklyTickLabels = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun','Sun 23:00'];

    const TABS = [
        { id: 'yearly',  label: '8760-Hour Profile' },
        { id: 'daily',   label: 'Daily Energy' },
        { id: 'weekly',  label: 'Weekly Shape' },
        { id: 'avgDay',  label: 'Avg Daily Profile' },
        { id: 'peakDay', label: 'Peak Day' },
    ];

    return (
        <div className="fixed inset-0 z-[1000] bg-gray-950/70 backdrop-blur-[2px] overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
                <div className="w-full max-w-6xl max-h-[94vh] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/10 flex flex-col">

                    {/* Modal header */}
                    <div className="flex items-start justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
                        <div>
                            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">
                                {subtitle}
                            </p>
                            <h2 className="text-base font-bold text-gray-900 font-mono">{h3_index}</h2>
                        </div>
                        <button
                            onClick={onClose}
                            className="ml-4 flex-shrink-0 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                        >
                            Close
                        </button>
                    </div>

                    <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">

                        {/* Key metrics */}
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                            {[
                                { label: 'Annual Energy', value: formatEnergyKwh(annualKwh) },
                                { label: 'Peak Demand',   value: formatPowerKw(peakKw) },
                                { label: 'Load Factor',   value: lf != null ? `${(lf * 100).toFixed(1)}%` : '—' },
                                { label: 'Peak Day',      value: profile.peak_day_date || '—' },
                                { label: 'Non-zero Hours', value: profile.profile_summary?.non_zero_hours ?? '—' },
                            ].map(({ label, value }) => (
                                <div key={label} className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                                    <p className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{label}</p>
                                    <p className="mt-0.5 text-lg font-bold text-gray-800">{value}</p>
                                </div>
                            ))}
                        </div>

                        {/* Tab bar */}
                        <div className="flex flex-wrap items-center gap-1.5">
                            {TABS.map(({ id, label }) => (
                                <button
                                    key={id}
                                    onClick={() => setTab(id)}
                                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                                        tab === id
                                            ? 'bg-blue-600 text-white shadow-sm'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Zoom control (yearly only) */}
                        {tab === 'yearly' && (
                            <div className="flex items-center gap-3 rounded-lg bg-gray-50 border border-gray-200 px-4 py-2">
                                <label className="text-xs font-medium text-gray-600">Zoom</label>
                                <input
                                    type="range" min={1} max={50} step={1} value={zoom}
                                    onChange={e => setZoom(Number(e.target.value))}
                                    className="w-44 accent-blue-600"
                                />
                                <span className="text-xs text-gray-500 w-12">{zoom}×</span>
                                <span className="text-xs text-blue-500">Drag chart to scroll</span>
                            </div>
                        )}

                        {/* Season / day-type selector */}
                        {(tab === 'weekly' || tab === 'avgDay') && (
                            <div className="flex flex-wrap gap-2">
                                <div className="flex flex-wrap gap-1">
                                    {ALL_SEASONS.map(s => (
                                        <button key={s} onClick={() => setSeason(s)}
                                            className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                                                season === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}>
                                            {s}
                                        </button>
                                    ))}
                                </div>
                                {tab === 'avgDay' && (
                                    <div className="flex flex-wrap gap-1">
                                        {ALL_DAY_TYPES.map(dt => (
                                            <button key={dt} onClick={() => setDayType(dt)}
                                                className={`px-2.5 py-1 rounded-md text-xs font-medium capitalize transition-colors ${
                                                    dayType === dt ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}>
                                                {dt}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── Chart panels ── */}

                        {tab === 'yearly' && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-400">Full {hourly8760.length}-hour load profile for the forecast year. Drag to scroll; use zoom slider to expand.</p>
                                <InteractiveTimelineChart
                                    values={hourly8760}
                                    zoom={zoom}
                                    xTickPositions={yearlyTickPos}
                                    xTickLabels={yearlyTickLabels}
                                    xAxisLabel={`Forecast year (Jan–Dec ${forecastYearLabel})`}
                                    yAxisLabel="Load"
                                    showWeekMarkers
                                    enableAdaptiveGrid
                                    axisStartDate={axisStart}
                                />
                            </div>
                        )}

                        {tab === 'daily' && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-400">Total energy charged at this hexagon per calendar day across the forecast year.</p>
                                <DailyEnergyChart dailyValues={dailyEnergy} color="#2563eb" />
                            </div>
                        )}

                        {tab === 'weekly' && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-400 capitalize">
                                    Average week (Mon–Sun) load shape · {season} season.
                                </p>
                                <InteractiveTimelineChart
                                    values={weeklyVals}
                                    color={season === 'all' ? '#2563eb' : SEASON_COLORS[season] || '#2563eb'}
                                    width={1100}
                                    zoom={1}
                                    xTickPositions={weeklyTickPos}
                                    xTickLabels={weeklyTickLabels}
                                    xAxisLabel={`Average week by hour (${season})`}
                                    yAxisLabel="Load"
                                />
                            </div>
                        )}

                        {tab === 'avgDay' && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-400 capitalize">
                                    Average 24-hour daily profile · {season} / {dayType}.
                                </p>
                                <InteractiveTimelineChart
                                    values={avgDailyVals}
                                    color={season === 'all' ? '#2563eb' : SEASON_COLORS[season] || '#2563eb'}
                                    width={900}
                                    zoom={1}
                                    xTickPositions={[0, 3, 6, 9, 12, 15, 18, 21, 23]}
                                    xTickLabels={['00','03','06','09','12','15','18','21','23']}
                                    xAxisLabel="Hour of day"
                                    yAxisLabel="Load"
                                />
                            </div>
                        )}

                        {tab === 'peakDay' && (
                            <div className="space-y-2">
                                <p className="text-xs text-gray-400">
                                    Hourly load profile on the peak demand day
                                    {profile.peak_day_date ? ` (${profile.peak_day_date})` : ''}.
                                    Peak: {formatPowerKw(profile.peak_day_peak_kw)}.
                                </p>
                                <InteractiveTimelineChart
                                    values={peakDayProfile}
                                    color="#dc2626"
                                    width={900}
                                    zoom={1}
                                    xTickPositions={[0, 3, 6, 9, 12, 15, 18, 21, 23]}
                                    xTickLabels={['00','03','06','09','12','15','18','21','23']}
                                    xAxisLabel="Hour of day (peak day)"
                                    yAxisLabel="Load"
                                />
                            </div>
                        )}

                        {/* Seasonal comparison grid */}
                        {tab === 'avgDay' && seasonalProfiles && (
                            <div className="mt-2">
                                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">All Seasons · {dayType}</p>
                                <div className="grid grid-cols-2 gap-4">
                                    {['spring','summer','fall','winter'].map(s => {
                                        const vals = seasonalProfiles[s]?.[dayType] || seasonalProfiles[s]?.all || [];
                                        return (
                                            <div key={s} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                                                <p className="text-xs font-semibold capitalize mb-2" style={{ color: SEASON_COLORS[s] }}>{s}</p>
                                                <InteractiveTimelineChart
                                                    values={vals}
                                                    color={SEASON_COLORS[s]}
                                                    width={480}
                                                    height={180}
                                                    zoom={1}
                                                    xTickPositions={[0, 6, 12, 18, 23]}
                                                    xTickLabels={['00','06','12','18','23']}
                                                    xAxisLabel="Hour"
                                                    yAxisLabel="Load"
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
